/**
 * Autonomy settings types — frontend mirror of the Builder Service response
 * shape for `GET/PUT /api/projects/:pid/autonomy/v1/settings` (J6, Wave 3,
 * plan §2 inter-lane contract). Keep field names in sync with the backend
 * (`AutonomySettingsService` / `AutonomyController`); the frontend never
 * derives its own verdicts — locks, selectability, ceilings, enablement
 * done-ness are all server-computed fields rendered as-is.
 */

export const AUTONOMY_LEVERS = ['copy', 'layout', 'routing', 'experiments', 'pricing'] as const;
export type AutonomyLever = (typeof AUTONOMY_LEVERS)[number];

export const AUTONOMY_TIERS = ['L1', 'L2', 'L3', 'L4', 'L5'] as const;
export type AutonomyTier = (typeof AUTONOMY_TIERS)[number];

/** One selectable/locked tier cell of a lever row — verdicts come from the server. */
export interface AutonomyTierOption {
  tier: AutonomyTier;
  selectable: boolean;
  locked: boolean;
  /** Present when locked — e.g. AUTONOMY_TIER_LOCK_REASON / AUTONOMY_PRICING_CEILING_REASON. */
  lockReason?: string;
}

export interface AutonomyLeverState {
  lever: AutonomyLever;
  currentTier: AutonomyTier;
  /** Server-computed ceiling (pricing is permanently capped at L4). */
  ceiling: AutonomyTier;
  tierOptions: AutonomyTierOption[];
  /** Honest caveat, e.g. routing: "No routing tool exists yet — this setting enforces nothing …". */
  note?: string;
}

export interface AutonomyEnablementItem {
  id: string;
  label: string;
  /** Server-computed — the frontend never derives done-ness. */
  done: boolean;
  detail?: string;
  /** In-app route (react-router path) when the item has a fix-it destination. */
  deepLink?: string;
}

export interface AutonomySettingsData {
  levers: AutonomyLeverState[];
  killSwitch: boolean;
  /** True when at least one lever resolved through the legacy proactiveFixes mapping. */
  resolvedFromLegacy?: boolean;
  updatedAt?: string;
  updatedBy?: string;
  enablement: AutonomyEnablementItem[];
}

/** PUT body — send only what changed; the backend merges per lever. */
export interface AutonomySettingsUpdate {
  levers?: Partial<Record<AutonomyLever, AutonomyTier>>;
  killSwitch?: boolean;
}

/**
 * Problem-style 400/422 codes surfaced by the settings PUT
 * (backend `AutonomyValidationCode` — keep the union in sync).
 */
export type AutonomySettingsErrorCode =
  | 'unknown_lever'
  | 'invalid_tier'
  | 'invalid_kill_switch'
  | 'tier_locked'
  | 'pricing_ceiling'
  | 'g3_composite_oec_required';
